import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Building2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { FieldError } from "@/components/FieldError";
import { getErrorMessage, getFieldErrors } from "@/lib/errors";

const EMPTY = { code: "", name: "", gstin: "", state: "", address: "" };

export default function Companies() {
  const { isOrgAdmin } = useAuth();
  const { refresh: refreshCompanyCtx } = useCompany() || {};
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/masters/companies");
      setCompanies(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (company) => {
    try {
      await api.patch(`/masters/companies/${company.id}`, { is_active: !company.is_active });
      load();
      refreshCompanyCtx?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="companies-page">
      <PageHeader
        title="Companies"
        description="Legal entities under your organisation (e.g. SA Construction, SARPL)."
        action={isOrgAdmin && <CreateCompanyDialog onCreated={() => { load(); refreshCompanyCtx?.(); }} />}
      />
      <div className="rounded-xl border border-border bg-card">
        {!loading && companies.length === 0 ? (
          <EmptyState title="No companies yet" description="Create your first legal entity to get started." testId="companies-empty-state" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>GSTIN</TableHead><TableHead>State</TableHead><TableHead>Status</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {companies.map((c) => (
                <TableRow key={c.id} data-testid={`company-row-${c.code}`}>
                  <TableCell className="font-mono text-xs text-foreground">{c.code}</TableCell>
                  <TableCell className="flex items-center gap-2 text-foreground"><Building2 className="h-3.5 w-3.5 text-muted-foreground" />{c.name}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{c.gstin || "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{c.state || "—"}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch checked={c.is_active} disabled={!isOrgAdmin} onCheckedChange={() => toggleActive(c)} data-testid={`company-active-toggle-${c.code}`} />
                      <Badge variant={c.is_active ? "default" : "secondary"}>{c.is_active ? "Active" : "Inactive"}</Badge>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function CreateCompanyDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSaving(true);
    try {
      await api.post("/masters/companies", form);
      toast.success(`Company ${form.code} created`);
      setForm(EMPTY);
      setOpen(false);
      onCreated?.();
    } catch (err) {
      setErrors(getFieldErrors(err));
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-company-button"><Plus className="h-4 w-4" /> New Company</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New Company</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="create-company-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Code</Label>
              <Input required data-testid="company-code-input" value={form.code} onChange={set("code")} className="mt-1.5" placeholder="SAC" />
              <FieldError error={errors.code} />
            </div>
            <div>
              <Label>Name</Label>
              <Input required data-testid="company-name-input" value={form.name} onChange={set("name")} className="mt-1.5" placeholder="SA Construction" />
              <FieldError error={errors.name} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>GSTIN</Label>
              <Input data-testid="company-gstin-input" value={form.gstin} onChange={set("gstin")} className="mt-1.5" />
            </div>
            <div>
              <Label>State</Label>
              <Input data-testid="company-state-input" value={form.state} onChange={set("state")} className="mt-1.5" placeholder="Rajasthan" />
            </div>
          </div>
          <div>
            <Label>Address</Label>
            <Textarea data-testid="company-address-input" value={form.address} onChange={set("address")} className="mt-1.5" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-company-submit-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
